import { CreditScoreGraph } from "shared/app/CreditScoreGraph";
import { Card } from "shared/components/Card";
import { cx, has, toMoney } from "shared/utils";

import { CriteriaBadgeIcon } from "shared/components/CriteriaBadgeIcon";
import { ApplicantSettings } from "shared/app/VerificationTemplate/slices/DecisionCriteria/types";
import { CriteriaTypeOptions } from "shared/app/VerificationTemplate/slices/DecisionCriteria/constants";
import {
  RecommendationMethod,
  GroupStat
} from "types/landlord/verificationTemplate";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { useMemo, useState } from "react";
import { Loader } from "shared/components/Loader";
import { Applicant } from "types/application";
import { ApplicantType, VerificationNames } from "shared/types";
import { Checkbox, Show } from "shared/components";
import { PermittedContent } from "../PermittedContent";
import { useAuthContext } from "providers/Auth";
import { VerifiedBadge } from "shared/components/VerifiedBadge";

const Stat = ({
  label,
  value,
  criteriaImportance,
  hasCriteria,
  criteriaSuccess,
  valueType = "money",
  noBorders = false,
  isPermitted,
  isVerified,
  isSelfAttested
}: {
  label: string;
  value?: number | null;
  criteriaImportance?: number | null;
  hasCriteria?: boolean;
  criteriaSuccess?: boolean;
  valueType?: "percentage" | "ratio" | "credit_score" | "money";
  selfReportedValue?: number | null;
  noBorders?: boolean;
  isPermitted: boolean;
  isVerified?: boolean;
  isSelfAttested?: boolean;
}) => {
  const normalizedValue = useMemo(() => {
    if (value === undefined || value === null) return "-";

    if (["ratio", "percentage"].includes(valueType))
      return `${value.toFixed(2)}${valueType === "ratio" ? "x" : "%"}`;

    if (valueType === "credit_score") return value.toFixed(0);

    return toMoney(value);
  }, [valueType, value]);

  return (
    <div
      className={cx(
        "w-full h-[136px] px-6 flex flex-col gap-2 py-6",
        noBorders ? "" : "border-b border-l border-alpha-40"
      )}
    >
      <h3 className="text-sm font-medium text-content-1000">{label}</h3>
      <PermittedContent isPermitted={isPermitted} protectedIconSize={32}>
        <p className="text-xl flex items-center gap-2 font-bold text-content-1000">
          {criteriaImportance == null ? null : (
            <CriteriaBadgeIcon
              hasCriteria={Boolean(hasCriteria)}
              success={Boolean(criteriaSuccess)}
            />
          )}

          {normalizedValue}
        </p>
        <Show if={isVerified}>
          <VerifiedBadge />
        </Show>
        <Show if={isSelfAttested}>
          <p className="text-content-300 text-sm font-medium">Self-reported</p>
        </Show>
      </PermittedContent>
    </div>
  );
};

export const GroupStats = ({
  criteriaTypeOptions,
  criteriaSettings,
  recommendationMethod,
  groupStat,
  includeCoSignerInCriteriaCalculation,
  applicationId,
  applicants
}: {
  criteriaTypeOptions: CriteriaTypeOptions;
  criteriaSettings?: ApplicantSettings;
  recommendationMethod?: RecommendationMethod;
  groupStat: GroupStat;
  isRuleBased?: boolean;
  includeCoSignerInCriteriaCalculation?: boolean;
  applicationId: string;
  applicants?: Applicant[];
}) => {
  const { permissions } = useAuthContext();
  const queryClient = useQueryClient();
  const [includeCoSigner, setIncludeCoSigner] = useState<boolean>(
    includeCoSignerInCriteriaCalculation || false
  );
  const { mutate: handleIncludeCoSigner, isPending: includeCoSignerPending } =
    useMutation({
      mutationKey: ["APPLICATION", applicationId],
      mutationFn: () => {
        return instance.patch(
          `portal/v1/applications/${applicationId}/include_co_signer`,
          {
            include_co_signer: !includeCoSigner
          }
        );
      },
      onSuccess: () => {
        setIncludeCoSigner(!includeCoSigner);
        queryClient.invalidateQueries({
          queryKey: ["APPLICATION", applicationId]
        });
      },
      onError: (err) => {
        toast.error(getErrorMessage(err.message));
      }
    });

  if (!Object.values(groupStat).filter((v) => Boolean(v)).length) return null;

  const isCombinedIncome = applicants?.some((applicant) =>
    applicant.verifications.find(
      (v) => v.name === VerificationNames.CombinedIncome
    )
  );

  const hasCreditScore = Boolean(groupStat.max_credit_score);
  const hasIncome = Boolean(
    has(groupStat.gross_income) ||
      has(groupStat.net_income) ||
      has(groupStat.self_reported_gross_income)
  );

  if (!hasIncome && !hasCreditScore) return null;

  if (!hasIncome)
    return (
      <Card className="w-full flex flex-col max-w-[1500px]">
        <div className="flex">
          <Stat
            noBorders
            label="Average credit score"
            valueType="credit_score"
            hasCriteria={
              criteriaTypeOptions === CriteriaTypeOptions.AVERAGE &&
              !!criteriaSettings?.credit_score?.value &&
              !!groupStat["average_credit_score"] &&
              recommendationMethod === "score_based"
            }
            criteriaSuccess={
              (groupStat["average_credit_score"] || 0) >=
              Number(criteriaSettings?.credit_score?.value || 0)
            }
            criteriaImportance={criteriaSettings?.credit_score?.importance}
            value={groupStat["average_credit_score"]}
            isPermitted={permissions.canAccessReports.creditReport}
          />
          <Stat
            noBorders
            label="Lowest credit score"
            valueType="credit_score"
            hasCriteria={
              criteriaTypeOptions === CriteriaTypeOptions.MINIMUM &&
              !!criteriaSettings?.credit_score?.value &&
              !!groupStat["min_credit_score"] &&
              recommendationMethod === "score_based"
            }
            criteriaSuccess={
              (groupStat["min_credit_score"] || 0) >=
              Number(criteriaSettings?.credit_score?.value || 0)
            }
            criteriaImportance={criteriaSettings?.credit_score?.importance}
            value={groupStat["min_credit_score"]}
            isPermitted={permissions.canAccessReports.creditReport}
          />

          <Stat
            noBorders
            label="Highest credit score"
            valueType="credit_score"
            hasCriteria={
              criteriaTypeOptions === CriteriaTypeOptions.MAXIMUM &&
              !!criteriaSettings?.credit_score?.value &&
              !!groupStat["max_credit_score"] &&
              recommendationMethod === "score_based"
            }
            criteriaSuccess={
              (groupStat["max_credit_score"] || 0) >=
              Number(criteriaSettings?.credit_score?.value || 0)
            }
            criteriaImportance={criteriaSettings?.credit_score?.importance}
            value={groupStat["max_credit_score"]}
            isPermitted={permissions.canAccessReports.creditReport}
          />

          <Stat
            noBorders
            label="People with no credit score"
            valueType="credit_score"
            hasCriteria={false}
            criteriaSuccess={false}
            criteriaImportance={undefined}
            value={groupStat["no_credit_score_people"]}
            isPermitted={permissions.canAccessReports.creditReport}
          />
        </div>
        <Show if={permissions.canAccessReports.incomeReport}>
          <div className="px-6 mt-auto pb-4">
            <CreditScoreGraph
              score={
                groupStat.average_credit_score == 4
                  ? 0
                  : groupStat.average_credit_score || 0
              }
            />
          </div>
        </Show>
      </Card>
    );

  return (
    <Card className="w-full flex flex-col max-w-[1500px]">
      <div className="flex items-center justify-between px-6 py-4 border-b border-alpha-40">
        <h3 className="text-base font-medium text-content-700">
          Group metrics
        </h3>
        <div className="flex items-center gap-4">
          {includeCoSignerPending ? (
            <Loader size={25} />
          ) : (
            <Checkbox
              wrapperClassName="row-reverse"
              label="Include guarantors"
              disabled={
                applicants?.filter(
                  (applicant) => applicant.role === ApplicantType.CoSigner
                ).length === 0
              }
              checked={Boolean(includeCoSigner)}
              onChange={() => handleIncludeCoSigner()}
            />
          )}
        </div>
      </div>

      <div className="grid [grid-template-columns:repeat(auto-fit,minmax(225px,1fr))] h-full">
        <Stat
          label="Gross income"
          isVerified
          value={groupStat["gross_income"]}
          hasCriteria={
            Boolean(criteriaSettings?.gross_income) &&
            Boolean(groupStat.gross_income) &&
            recommendationMethod === "score_based"
          }
          criteriaSuccess={
            (groupStat.gross_income || 0) >=
            Number(criteriaSettings?.gross_income?.value || 0)
          }
          criteriaImportance={criteriaSettings?.gross_income?.importance}
          isPermitted={permissions.canAccessReports.incomeReport}
        />

        <Stat
          label="Gross income to rent"
          isVerified
          valueType="ratio"
          value={groupStat["gross_income_to_rent_ratio"]}
          hasCriteria={
            !!criteriaSettings?.gross_income_to_rent_ratio &&
            !!groupStat["gross_income_to_rent_ratio"] &&
            recommendationMethod === "score_based"
          }
          criteriaSuccess={
            (groupStat["gross_income_to_rent_ratio"] || 0) >=
            Number(criteriaSettings?.gross_income_to_rent_ratio?.value || 0)
          }
          criteriaImportance={
            criteriaSettings?.gross_income_to_rent_ratio?.importance
          }
          isPermitted={permissions.canAccessReports.incomeReport}
        />

        <Stat
          label="Debt to gross income"
          isVerified
          value={
            groupStat["debt_to_gross_income"]
              ? groupStat["debt_to_gross_income"] * 100
              : groupStat["debt_to_gross_income"]
          }
          valueType="percentage"
          hasCriteria={
            !!criteriaSettings?.debt_to_gross_income &&
            !!groupStat["debt_to_gross_income"] &&
            recommendationMethod === "score_based"
          }
          criteriaSuccess={
            (groupStat["debt_to_gross_income"] || 0) <=
            Number(
              String(criteriaSettings?.debt_to_gross_income?.value).replace(
                "%",
                ""
              ) || 0
            )
          }
          criteriaImportance={
            criteriaSettings?.debt_to_gross_income?.importance
          }
          isPermitted={permissions.canAccessReports.incomeReport}
        />

        <Stat
          label="Debt to net income"
          isVerified
          value={
            isCombinedIncome && groupStat["debt_to_net_income"]
              ? groupStat["debt_to_net_income"] * 100
              : groupStat["debt_to_net_income"]
          }
          valueType="percentage"
          hasCriteria={
            !!criteriaSettings?.debt_to_net_income &&
            !!groupStat["debt_to_net_income"] &&
            recommendationMethod === "score_based"
          }
          criteriaSuccess={
            (groupStat["debt_to_net_income"] || 0) <=
            Number(
              String(criteriaSettings?.debt_to_net_income?.value).replace(
                "%",
                ""
              ) || 0
            )
          }
          criteriaImportance={criteriaSettings?.debt_to_net_income?.importance}
          isPermitted={permissions.canAccessReports.incomeReport}
        />

        <Stat
          label="Net income"
          isVerified
          value={groupStat["net_income"]}
          hasCriteria={
            !!criteriaSettings?.net_income &&
            !!groupStat["net_income"] &&
            recommendationMethod === "score_based"
          }
          criteriaSuccess={
            (groupStat["net_income"] || 0) >=
            Number(criteriaSettings?.net_income?.value || 0)
          }
          criteriaImportance={criteriaSettings?.net_income?.importance}
          isPermitted={permissions.canAccessReports.incomeReport}
        />

        <Stat
          label="Highest credit score"
          valueType="credit_score"
          hasCriteria={false}
          criteriaSuccess={false}
          criteriaImportance={undefined}
          value={groupStat["max_credit_score"]}
          isPermitted={permissions.canAccessReports.creditReport}
        />

        <Stat
          label="Gross income"
          isSelfAttested
          value={groupStat["self_reported_gross_income"]}
          isPermitted={permissions.canAccessReports.incomeReport}
        />

        <Stat
          label="Gross income to rent"
          isSelfAttested
          valueType="ratio"
          value={groupStat["self_reported_gross_income_to_rent"]}
          isPermitted={permissions.canAccessReports.incomeReport}
        />

        <Stat
          label="Debt to gross income"
          isSelfAttested
          value={
            groupStat["debt_to_self_reported_gross_income"]
              ? groupStat["debt_to_self_reported_gross_income"] * 100
              : groupStat["debt_to_self_reported_gross_income"]
          }
          valueType="percentage"
          hasCriteria={
            !!criteriaSettings?.debt_to_self_reported_gross_income &&
            !!groupStat["debt_to_self_reported_gross_income"] &&
            recommendationMethod === "score_based"
          }
          criteriaSuccess={
            (groupStat["debt_to_self_reported_gross_income"] || 0) <=
            Number(
              String(
                criteriaSettings?.debt_to_self_reported_gross_income?.value
              ).replace("%", "") || 0
            )
          }
          criteriaImportance={
            criteriaSettings?.debt_to_self_reported_gross_income?.importance
          }
          isPermitted={permissions.canAccessReports.incomeReport}
        />

        <Stat
          label="People with no credit score"
          valueType="credit_score"
          hasCriteria={false}
          criteriaSuccess={false}
          criteriaImportance={undefined}
          value={groupStat["no_credit_score_people"]}
          isPermitted={permissions.canAccessReports.creditReport}
        />

        <Stat
          label="Average credit score"
          valueType="credit_score"
          hasCriteria={
            criteriaTypeOptions === CriteriaTypeOptions.AVERAGE &&
            !!criteriaSettings?.credit_score &&
            !!groupStat["average_credit_score"] &&
            recommendationMethod === "score_based"
          }
          criteriaSuccess={
            (groupStat["average_credit_score"] || 0) >=
              Number(criteriaSettings?.credit_score?.value || 0) &&
            recommendationMethod === "score_based"
          }
          criteriaImportance={criteriaSettings?.credit_score?.importance}
          value={
            groupStat["average_credit_score"] == 4
              ? undefined
              : groupStat["average_credit_score"]
          }
          isPermitted={permissions.canAccessReports.creditReport}
        />

        <Stat
          label="Lowest credit score"
          valueType="credit_score"
          hasCriteria={false}
          criteriaSuccess={false}
          criteriaImportance={undefined}
          value={groupStat["min_credit_score"]}
          isPermitted={permissions.canAccessReports.creditReport}
        />
      </div>
    </Card>
  );
};
